import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import { SHAPE_BOUNDS, UNIT } from './patternShapesConstants';
/**
 * Single Tab stop / focus host for all user-converted (`isStaticOutline`) pattern
 * shapes on the canvas.
 *
 * The wrapper itself is sized to the AABB of all child outlines so that:
 *   1. Native :focus-visible on the wrapper paints exactly one ring around the AABB.
 *   2. ContextMenu's positioning logic — which queries `[data-manipulative-id]` and
 *      uses that element's getBoundingClientRect() — anchors the menu next to the AABB.
 *   3. ContextMenu's closeMenu finds the same focusable wrapper and restores focus.
 *
 * Children are rendered as siblings (not descendants) so their canvas-coord absolute
 * positioning resolves against the canvas content div, not the AABB-sized wrapper.
 */
var PatternStaticOutlineCohort = function (_a) {
    var items = _a.items, renderItem = _a.renderItem;
    var _b = useContext(DataContext), selectedTool = _b.selectedTool, setSelectedTool = _b.setSelectedTool, setContextMenu = _b.setContextMenu;
    var t = useTranslation().t;
    var wrapperRef = useRef(null);
    var ids = items.map(function (i) { return i.data.id; });
    // Union AABB of all static outlines.
    var bounds = useMemo(function () {
        if (items.length === 0)
            return null;
        var minX = Infinity;
        var minY = Infinity;
        var maxX = -Infinity;
        var maxY = -Infinity;
        items.forEach(function (item) {
            var _a;
            var d = item.data;
            var sb = SHAPE_BOUNDS[d.shapeType] || { x: 0, y: 0, w: UNIT, h: UNIT };
            var scale = (_a = d.scale) !== null && _a !== void 0 ? _a : 1;
            minX = Math.min(minX, d.center.x);
            minY = Math.min(minY, d.center.y);
            maxX = Math.max(maxX, d.center.x + sb.w * scale);
            maxY = Math.max(maxY, d.center.y + sb.h * scale);
        });
        return { minX: minX, minY: minY, width: maxX - minX, height: maxY - minY };
    }, [items]);
    var handleFocus = function () {
        var same = ids.length === selectedTool.length && ids.every(function (oid) { return selectedTool.includes(oid); });
        if (!same)
            setSelectedTool(ids);
    };
    var handleKeyDown = function (e) {
        var _a;
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            // Provide x/y as a fallback; ContextMenu primarily positions itself off the
            // element matching contextMenu.manipulativeId — which is THIS wrapper, sized
            // to the AABB — so the menu naturally lands at the cohort's right-top.
            var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect && ids.length > 0) {
                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: ids[0] });
            }
        }
    };
    if (!bounds)
        return null;
    return (_jsxs(Fragment, { children: [_jsx("div", { ref: wrapperRef, className: "staticOutlineCohort", tabIndex: 0, role: "group", "aria-label": "".concat(t('text.outline'), " of ").concat(items.length, " ").concat(t('text.patternShape').toLowerCase()).concat(items.length === 1 ? '' : 's', ". Press Enter for options."), "data-manipulative-id": ids[0], onFocus: handleFocus, onKeyDown: handleKeyDown, style: {
                    position: 'absolute',
                    left: bounds.minX,
                    top: bounds.minY,
                    width: bounds.width,
                    height: bounds.height,
                    // Mouse interactions belong to the underlying shape SVGs; the wrapper is
                    // a focus host only.
                    pointerEvents: 'none',
                } }), items.map(function (item) { return renderItem(item.data); })] }));
};
export default PatternStaticOutlineCohort;
